/**
 * お問い合わせ本文の合成（ご参加区分・紹介者）
 * - 目的: 「誰からの紹介か」を専用カラムを増やさずに把握するため、
 *   申込者の区分と紹介者名をお問い合わせ本文の先頭に定型で埋め込む
 * - 保存先は inquiries.message のままなので、管理画面・通知メールにもそのまま表示される
 */

/**
 * 区分・紹介者を尋ねるお問い合わせ種別
 * - 目的: イベント申込みと入会相談のときだけ追加項目を出す
 */
export const REFERRAL_TARGET_TYPES = ['入会希望', 'イベント参加'] as const;

/**
 * 申込者の区分（表示順）
 */
export const ATTENDEE_TYPES = ['有料会員', '無料会員', '初めて'] as const;

export type AttendeeType = (typeof ATTENDEE_TYPES)[number];

/**
 * 初めての参加者だけに紹介者を尋ねる
 */
export const FIRST_TIME_ATTENDEE_TYPE: AttendeeType = '初めて';

export interface InquiryMessageInput {
  /** お問い合わせ種別 */
  type: string;
  /** ご自身の区分（未選択は空文字） */
  attendeeType: '' | AttendeeType;
  /** 紹介者のお名前（任意） */
  referrerName: string;
  /** 「紹介者はいません」チェック */
  noReferrer: boolean;
  /** ユーザーが入力した本文 */
  message: string;
}

/**
 * 追加項目を表示する種別かどうかを判定
 * - 引数: type (string) お問い合わせ種別
 * - 戻り値: boolean
 */
export function isReferralTargetType(type: string): boolean {
  return (REFERRAL_TARGET_TYPES as readonly string[]).includes(type);
}

/**
 * 送信する本文を組み立てる
 * - 目的: 区分・紹介者を本文先頭のプレフィックスに変換する
 * - 引数: input (InquiryMessageInput)
 * - 戻り値: string（対象外の種別・区分未選択のときは本文をそのまま返す）
 */
export function buildInquiryMessage(input: InquiryMessageInput): string {
  if (!isReferralTargetType(input.type) || !input.attendeeType) {
    return input.message;
  }

  const lines = [`【ご参加区分】${input.attendeeType}`];

  if (input.attendeeType === FIRST_TIME_ATTENDEE_TYPE) {
    const referrer = input.noReferrer ? 'なし' : input.referrerName.trim();
    if (referrer) lines.push(`【紹介者】${referrer}`);
  }

  return `${lines.join('\n')}\n\n${input.message}`;
}
